import { useCallback, useEffect, useState } from 'react';
import { useRegisterSW } from 'virtual:pwa-register/react';

/**
 * Install and update affordances.
 *
 * Two very different install stories:
 *  - Chromium fires `beforeinstallprompt`, which we capture and replay on a real tap.
 *  - iOS Safari has no such event and never will. Installing is a manual walk through
 *    Safari's own menus, so all we can do is detect iOS-in-a-browser and describe the
 *    taps. The steps themselves live in one place — `IosInstallSteps` in ui.tsx — because
 *    they are a claim about somebody else's software and Apple keeps moving the buttons.
 */

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    // iOS exposes standalone on navigator rather than through the media query.
    (window.navigator as Navigator & { standalone?: boolean }).standalone === true
  );
}

export function isIos(): boolean {
  if (typeof navigator === 'undefined') return false;
  return (
    /iphone|ipad|ipod/i.test(navigator.userAgent) ||
    // iPadOS 13+ reports itself as a Mac; the touch points give it away.
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  );
}

export interface PwaState {
  /** Non-null when the browser will show a native install prompt. */
  promptInstall: (() => void) | null;
  /** True when we should explain the iOS Share-sheet route instead. */
  showIosInstallHint: boolean;
  updateReady: boolean;
  applyUpdate: () => void;
}

export function usePwa(): PwaState {
  const [installEvent, setInstallEvent] = useState<BeforeInstallPromptEvent | null>(null);

  const {
    needRefresh: [needRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegisterError(error) {
      // A failed SW registration must never break the app; it only costs offline caching.
      console.warn('Service worker registration failed', error);
    },
  });

  useEffect(() => {
    const handler = (event: Event) => {
      // Suppress the browser's own mini-infobar so the prompt appears where our UI offers it.
      event.preventDefault();
      setInstallEvent(event as BeforeInstallPromptEvent);
    };
    window.addEventListener('beforeinstallprompt', handler);
    const installed = () => setInstallEvent(null);
    window.addEventListener('appinstalled', installed);
    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
      window.removeEventListener('appinstalled', installed);
    };
  }, []);

  const promptInstall = useCallback(() => {
    if (!installEvent) return;
    void installEvent.prompt().then(() => setInstallEvent(null));
  }, [installEvent]);

  const applyUpdate = useCallback(() => {
    void updateServiceWorker(true);
  }, [updateServiceWorker]);

  return {
    promptInstall: installEvent ? promptInstall : null,
    showIosInstallHint: isIos() && !isStandalone(),
    updateReady: needRefresh,
    applyUpdate,
  };
}
